import React, { useState } from 'react';
import { GalleryItem } from '../types';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

interface MediaGalleryProps {
  items: GalleryItem[];
}

const MediaGallery = ({ items }: MediaGalleryProps) => {
  // Guard against empty items — must be before hooks to avoid conditional hook calls
  // Actually, we must call hooks unconditionally. So we use a safe default.
  const safeItems = items && items.length > 0 ? items : [];
  const [selectedItem, setSelectedItem] = useState<GalleryItem | null>(safeItems[0] || null);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [isFullscreen, setIsFullscreen] = useState(false);

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
    if (!isFullscreen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
  };

  // Early return AFTER hooks
  if (!safeItems.length || !selectedItem) {
    return (
      <section id="gallery" className="mb-24">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <h2 className="text-3xl font-black text-[#0C2C39] mb-4 tracking-tight uppercase">
              Project Media <span className="text-[#0D9488]">Gallery</span>
            </h2>
            <p className="text-[#0C2C39]/60 font-medium max-w-xl">
              No media content available for this project yet.
            </p>
          </div>
        </div>
      </section>
    );
  }

  const categories = ['all', ...Array.from(new Set(safeItems.map(img => img.category)))];

  const filteredItems = selectedCategory === 'all'
    ? safeItems
    : safeItems.filter(img => img.category === selectedCategory);

  const getCategoryLabel = (category: string) => {
    switch (category.toLowerCase()) {
      case 'desktop': return 'Desktop';
      case 'mobile': return 'Mobile';
      case 'wireframe': return 'Wireframes';
      case 'design': return 'Design';
      case 'all': return 'All';
      default: return category.charAt(0).toUpperCase() + category.slice(1);
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category.toLowerCase()) {
      case 'desktop': return 'Monitor';
      case 'mobile': return 'Smartphone';
      case 'wireframe': return 'Layout';
      case 'design': return 'Palette';
      case 'all': return 'Grid';
      default: return 'Layers';
    }
  };

  const renderMedia = (item: GalleryItem) => {
    if (item.type === 'video' && item.videoUrl) {
      const isEmbed = item.videoUrl.includes('youtube.com') || item.videoUrl.includes('vimeo.com');

      if (isEmbed) {
        return (
          <iframe
            src={item.videoUrl}
            title={item.title}
            className="w-full h-full border-0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        );
      }
      return (
        <video
          src={item.videoUrl}
          controls
          className="w-full h-full object-contain"
          poster={item.src}
        />
      );
    }

    return (
      <Image
        src={item.src}
        alt={item.alt}
        className="w-full h-full object-contain"
      />
    );
  };

  return (
    <section id="gallery" className="mb-24">
      {/* Fullscreen Lightbox Overlay */}
      {isFullscreen && (
        <div className="fixed inset-0 z-[100] flex flex-col bg-[#0C2C39]/95 backdrop-blur-xl p-4 md:p-12">
          <div className="flex justify-between items-center mb-8">
            <div className="flex flex-col">
              <h3 className="text-2xl font-black text-white uppercase tracking-tight">{selectedItem.title}</h3>
              <p className="text-[#0D9488] font-bold text-xs uppercase tracking-widest">{selectedItem.category}</p>
            </div>
            <button
              onClick={toggleFullscreen}
              className="p-4 bg-white/10 hover:bg-white/20 text-white rounded-2xl transition-all hover:scale-110"
            >
              <Icon name="X" size={24} />
            </button>
          </div>

          <div className="flex-1 relative flex items-center justify-center overflow-hidden rounded-3xl bg-black/20 border border-white/10">
            {renderMedia(selectedItem)}
          </div>

          <div className="mt-8 flex justify-center gap-4 overflow-x-auto no-scrollbar py-4">
            {filteredItems.map((item) => (
              <button
                key={item.id}
                onClick={() => setSelectedItem(item)}
                className={`flex-shrink-0 w-24 md:w-32 aspect-video rounded-xl overflow-hidden border-2 transition-all ${selectedItem.id === item.id ? 'border-[#0D9488] ring-4 ring-[#0D9488]/20 scale-105' : 'border-transparent opacity-50 hover:opacity-100'
                  }`}
              >
                <img src={item.thumbnail} alt={item.alt} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
        <div>
          <h2 className="text-3xl font-black text-[#0C2C39] mb-4 tracking-tight uppercase">
            Project Media <span className="text-[#0D9488]">Gallery</span>
          </h2>
          <p className="text-[#0C2C39]/60 font-medium max-w-xl">
            A visual showcase of the project's interface, user flow, and key features across different platforms.
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap gap-2">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-6 py-2 rounded-full text-[11px] font-black tracking-widest uppercase transition-all duration-300 flex items-center gap-2 border ${selectedCategory === category
                ? 'bg-[#0C2C39] text-white border-[#0C2C39]'
                : 'bg-white text-[#0C2C39]/60 border-[#0C2C39]/10 hover:border-[#0C2C39]/40 hover:text-[#0C2C39]'
                }`}
            >
              <Icon name={getCategoryIcon(category)} size={14} />
              <span>{getCategoryLabel(category)}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Main Display */}
        <div className="lg:col-span-9">
          <div
            className="bg-white border-2 border-[#0C2C39] shadow-[8px_8px_0px_0px_#0C2C39] overflow-hidden rounded-2xl aspect-video relative group cursor-zoom-in"
            onClick={toggleFullscreen}
          >
            {renderMedia(selectedItem)}

            {selectedItem.type === 'video' && (
              <div className="absolute top-6 left-6 pointer-events-none">
                <div className="bg-[#0D9488] text-white px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest flex items-center gap-2">
                  <Icon name="Play" size={12} fill="currentColor" />
                  Video Presentation
                </div>
              </div>
            )}

            <div className="absolute inset-0 bg-[#0C2C39]/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              <div className="bg-white/90 backdrop-blur-sm text-[#0C2C39] px-6 py-3 rounded-2xl font-black uppercase tracking-widest text-[10px] shadow-2xl flex items-center gap-2">
                <Icon name="Maximize2" size={16} />
                Enter Fullscreen View
              </div>
            </div>
          </div>

          <div className="mt-8">
            <h3 className="text-2xl font-black text-[#0C2C39] mb-2 tracking-tight">
              {selectedItem.title}
            </h3>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2 px-3 py-1 bg-[#0C2C39]/5 rounded-lg">
                  <Icon name={getCategoryIcon(selectedItem.category)} size={14} className="text-[#0D9488]" />
                  <span className="text-[11px] font-bold text-[#0C2C39]/60 uppercase tracking-wider">
                    {selectedItem.category}
                  </span>
                </div>
                <p className="text-[#0C2C39]/50 text-sm font-medium">
                  {selectedItem.alt}
                </p>
              </div>

              <button
                onClick={toggleFullscreen}
                className="flex items-center gap-2 text-[10px] font-black text-[#0D9488] uppercase tracking-widest hover:text-[#0C2C39] transition-colors"
              >
                <Icon name="Maximize2" size={14} />
                Fullscreen Experience
              </button>
            </div>
            {selectedItem.type === 'image' && (
              <button
                onClick={() => window.open(selectedItem.src, '_blank')}
                className="mt-4 flex items-center gap-2 text-[10px] font-black text-[#0D9488]/40 uppercase tracking-widest hover:text-[#0C2C39] transition-colors"
              >
                <Icon name="ExternalLink" size={12} />
                Open Original File
              </button>
            )}
          </div>
        </div>

        {/* Thumbnails Sidebar */}
        <div className="lg:col-span-3">
          <div className="bg-[#FFF9EB] border-2 border-[#0C2C39]/5 rounded-2xl p-6 h-full">
            <h3 className="text-[11px] font-black text-[#0C2C39]/40 uppercase tracking-[0.2em] mb-6">
              Media Stream ({filteredItems.length})
            </h3>

            <div className="grid grid-cols-2 lg:grid-cols-1 gap-4 max-h-[600px] overflow-y-auto pr-2 no-scrollbar">
              {filteredItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => setSelectedItem(item)}
                  className={`group relative rounded-xl overflow-hidden aspect-video transition-all duration-300 border-2 ${selectedItem.id === item.id
                    ? 'border-[#0C2C39] ring-4 ring-[#0C2C39]/5 shadow-lg shadow-[#0C2C39]/10 scale-[1.02]'
                    : 'border-transparent opacity-60 hover:opacity-100 hover:border-[#0C2C39]/20'
                    }`}
                >
                  <Image
                    src={item.thumbnail}
                    alt={item.alt}
                    className="w-full h-full object-cover transition-all duration-500 group-hover:scale-110"
                  />

                  {item.type === 'video' && (
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                      <div className="w-8 h-8 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center border border-white/30 text-white">
                        <Icon name="Play" size={16} fill="currentColor" />
                      </div>
                    </div>
                  )}

                  <div className={`absolute inset-0 bg-[#0C2C39]/20 flex items-center justify-center transition-opacity duration-300 ${selectedItem.id === item.id ? 'opacity-0' : 'opacity-0 group-hover:opacity-100'
                    }`}>
                    <Icon name="Maximize2" size={20} className="text-white" />
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MediaGallery;